package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.data.remote.safeSnapshots
import com.evoapps.evoshift.data.dto.auth.BillingMirrorDto
import com.evoapps.evoshift.data.dto.auth.OwnerDto
import com.evoapps.evoshift.data.dto.auth.toBillingCustomer
import com.evoapps.evoshift.domain.billing.BillingCustomer
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.catch
import com.evoapps.evoshift.data.mapper.OwnerMapper
import com.evoapps.evoshift.domain.model.Owner
import com.evoapps.evoshift.domain.repository.OwnerRepository
import dev.gitlive.firebase.firestore.FieldValue
import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.data.mapper.ContractTypeMapper
import com.evoapps.evoshift.domain.model.ContractType

class OwnerRepositoryImpl(
    private val firestore: AppFirestore,
    private val ownerMapper: OwnerMapper,
    private val contractTypeMapper: ContractTypeMapper
) : OwnerRepository {

    override suspend fun getOwner(uid: String): Result<Owner?> {
        return try {
            val snapshot = firestore.collection("owners").document(uid).get()
            if (snapshot.exists) {
                val dto = snapshot.data<OwnerDto>()
                Result.success(ownerMapper.toDomain(dto))
            } else {
                Result.success(null)
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun createOwner(owner: Owner): Result<Unit> {
        return try {
            val dto = ownerMapper.toDto(owner)
            firestore.collection("owners").document(owner.uid).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun addWorkplaceToOwner(uid: String, workplaceId: String): Result<Unit> {
        return try {
            firestore.collection("owners").document(uid)
                .updateFields { "workplaceIds" to FieldValue.arrayUnion(workplaceId) }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateBusinessName(uid: String, newBusinessName: String): Result<Unit> {
        return try {
            firestore.collection("owners").document(uid)
                .updateFields { "businessName" to newBusinessName }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun recordDpaAcceptance(uid: String, version: String): Result<Unit> = runCatching {
        firestore.collection("owners").document(uid).updateFields {
            "dpaAcceptedVersion" to version
            "dpaAcceptedAt" to AppClock.now().toString()
        }
    }

    override fun observeBilling(uid: String): Flow<BillingCustomer> =
        firestore.collection("owners").document(uid).collection("billing").document("revenuecat")
            .safeSnapshots
            .map { snapshot ->
                if (!snapshot.exists) BillingCustomer(uid)
                // A field RevenueCat adds or changes must not break the plan: unreadable = none.
                else runCatching { snapshot.data<BillingMirrorDto>().toBillingCustomer(uid) }.getOrElse { BillingCustomer(uid) }
            }
            .distinctUntilChanged()

    override suspend fun getContractTypes(ownerId: String): Result<List<ContractType>> {
        return try {
            val snapshot = firestore.collection("owners").document(ownerId)
                .collection("contract_types").get()
            val types = snapshot.documents.map {
                contractTypeMapper.toDomain(it.data())
            }
            Result.success(types)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override fun observeContractTypes(ownerId: String): Flow<List<ContractType>> =
        firestore.collection("owners").document(ownerId).collection("contract_types")
            .snapshots
            .map { snapshot ->
                // Each document on its own: one unreadable type must not hide the others.
                snapshot.documents.mapNotNull { doc ->
                    runCatching { contractTypeMapper.toDomain(doc.data<com.evoapps.evoshift.data.dto.auth.ContractTypeDto>()).let { if (it.id.isBlank()) it.copy(id = doc.id) else it } }.getOrNull()
                }
            }
            // An operational manager may not read them (firestore.rules): the built-in contracts only.
            .catch { emit(emptyList()) }
            .distinctUntilChanged()

    override suspend fun addContractType(ownerId: String, contractType: ContractType): Result<String> {
        return try {
            val collectionRef = firestore.collection("owners").document(ownerId)
                .collection("contract_types")
            val newDocRef = collectionRef.document
            val newId = newDocRef.id
            val dto = contractTypeMapper.toDto(contractType.copy(id = newId))
            newDocRef.set(dto)
            Result.success(newId)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateContractType(ownerId: String, contractType: ContractType): Result<Unit> {
        return try {
            val dto = contractTypeMapper.toDto(contractType)
            firestore.collection("owners").document(ownerId)
                .collection("contract_types").document(contractType.id).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun deleteContractType(ownerId: String, contractTypeId: String): Result<Unit> {
        return try {
            firestore.collection("owners").document(ownerId)
                .collection("contract_types").document(contractTypeId).delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}
